<template>
  <div class="land-tab rpd-root">
    <!-- Page header -->
    <div class="lt-page-header">
      <div class="lt-eyebrow-row">
        <span class="lt-accent-bar"></span>
        <span class="lt-eyebrow lt-eyebrow-violet">Land tab · site &amp; parcel</span>
      </div>
      <h1 class="lt-title">{{ project.project_title || 'Land' }}</h1>
      <div class="lt-meta-line">
        <span class="lt-meta-cell">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s7-6.5 7-12a7 7 0 0 0-14 0c0 5.5 7 12 7 12Z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
          {{ cityName || '—' }}
        </span>
        <span class="lt-meta-sep">·</span>
        <span class="lt-meta-cell">{{ lotCount }} {{ lotCount === 1 ? 'lot' : 'lots' }} · {{ totalComponentCount }}
          project components</span>
      </div>
      <div v-if="canEdit" class="lt-header-actions">
        <button v-if="canManageProject" type="button" class="lt-btn lt-btn-primary" @click="openPermitForm()">
          + Permit Application
        </button>
        <button v-if="canManageProject" type="button" class="lt-btn lt-btn-secondary"
          @click="openCollectionForm('building')">
          + Building
        </button>
        <a class="lt-btn lt-btn-secondary" :href="editHref" target="_blank" rel="noopener noreferrer">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
          </svg>
          Edit
        </a>
      </div>
    </div>

    <!-- Stacked layout: horizontal Lot Navigator above the content -->
    <div class="lt-layout">
      <!-- Lot Navigator: horizontal strip at the top of the content area -->
      <nav class="lt-lot-nav">
        <span class="lt-eyebrow lt-eyebrow-muted lt-lot-nav-label">Parcels</span>
        <div class="lt-lot-nav-track">
          <button class="lt-lot-btn" :class="{ active: activeLotId === 'total' }" @click="selectLot('total')">
            <span class="lt-lot-kicker lt-kicker-coral">TOTAL</span>
            <span class="lt-lot-title">All components</span>
            <span class="lt-lot-sub">{{ totalComponentCount }} components rolled up</span>
          </button>
          <button v-for="(lot, idx) in developmentStats" :key="(lot.ptc && lot.ptc.fid) || idx" class="lt-lot-btn"
            :class="{ active: activeLotId === String(lot.ptc.fid) }" @click="selectLot(String(lot.ptc.fid))">
            <span class="lt-lot-kicker lt-kicker-violet">LOT {{ lot.ptc.lot || (idx + 1) }}</span>
            <span class="lt-lot-title">{{ lotAddress(lot) }}</span>
            <span class="lt-lot-sub">{{ lotSubLine(lot) }}</span>
          </button>
          <div v-if="developmentStats.length === 0" class="lt-empty-state">No parcels available.</div>
        </div>
      </nav>

      <!-- Content Area -->
      <section class="lt-content">
        <!-- TOTAL VIEW -->
        <div v-if="activeLotId === 'total'" class="lt-stack">
          <article class="lt-card lt-card-pad">
            <div class="lt-section-head">
              <span class="lt-section-dash"></span>
              <span class="lt-eyebrow lt-eyebrow-violet">Roll-up</span>
              <h2 class="lt-section-title">Site summary</h2>
            </div>
            <p class="lt-section-body">
              This site carries {{ totalComponentCount }} components across
              {{ ptcSections.length }} {{ ptcSections.length === 1 ? 'type collection' : 'type collections' }}.
              Select a parcel above to see deal terms, zoning, and the unit breakdown for that lot.
            </p>
            <div v-if="developmentTotals.composition && developmentTotals.composition.length"
              class="lt-composition-chips">
              <span v-for="(c, idx) in developmentTotals.composition" :key="idx" class="lt-chip">{{ c }}</span>
            </div>
          </article>

          <!-- Project type collections. One section per PTC: the parcels its
               building is approved on beside the unit type collections
               standing on them. Nested under the PTC rather than listed as two
               flat sections — a component is only readable against its own
               PTC, and with two PTCs on a site a flat component grid loses
               which building each one belongs to.
               These parcels are not the same set as the navigator above, which
               lists the presale node's own: a PTC carries the consolidated lot
               its building is approved on, and two PTCs can share an address.

               The tab strip stands above the sections whenever the project has
               a collection at all, so the reader can see how many the site
               carries before scrolling one of them. -->
          <nav v-if="ptcSections.length" class="lt-ptc-nav" role="tablist" aria-label="Project type collections">
            <button v-for="section in ptcSections" :key="section.key" type="button" role="tab" class="lt-ptc-nav-btn"
              :class="{ active: section.key === visiblePtcKey }"
              :aria-selected="section.key === visiblePtcKey ? 'true' : 'false'" @click="activePtcKey = section.key">
              <span class="lt-ptc-nav-kicker">{{ section.label }}</span>
              <span class="lt-ptc-nav-name">{{ section.navLabel }}</span>
              <span class="lt-ptc-nav-count">{{ section.components.length }} UTC</span>
            </button>
          </nav>

          <section v-for="section in ptcSections" v-show="section.key === visiblePtcKey" :key="section.key"
            class="lt-card lt-card-pad">
            <header class="lt-ptc-head">
              <span class="lt-section-dash"></span>
              <span class="lt-eyebrow lt-eyebrow-violet">{{ section.label }}</span>
              <h2 :class="['lt-ptc-title', { 'lt-fact-empty': !section.name }]">{{ section.name || '—' }}</h2>
              <span v-if="section.typeLabel" class="lt-ptc-meta">· {{ section.typeLabel }}</span>
              <span class="lt-ptc-counts">
                <span class="lt-chip">{{ section.lots.length }}
                  {{ section.lots.length === 1 ? 'land sale lot' : 'land sale lots' }}</span>
                <span class="lt-chip">{{ section.components.length }} {{ section.components.length === 1 ? 'UTC' :
                  'UTCs' }}</span>
              </span>
              <button v-if="canManageProject && section.fid" type="button" class="lt-col-add lt-head-add"
                @click="openSectionPermitForm('ptc', section)">
                + Permit Application
              </button>
            </header>

            <div class="lt-ptc-cols">
              <!-- Left column: the parcels this PTC's building is approved on -->
              <div class="lt-ptc-col">
                <div class="lt-ptc-col-head">
                  <span class="lt-eyebrow lt-eyebrow-muted">Land sale lots</span>
                  <span class="lt-ptc-col-count">{{ section.lots.length }}</span>
                </div>
                <div v-if="section.lots.length" class="lt-ptc-land-stack">
                  <section v-for="lot in section.lots" :key="lot.key" class="lt-ptc-land-card">
                    <header class="lt-ptc-land-top">
                      <span class="lt-ptc-land-ident">
                        <span class="lt-ptc-land-label">PTC project name</span>
                        <span :class="['lt-ptc-land-name', { 'lt-fact-empty': !lot.ptcName }]">{{ lot.ptcName || '—'
                        }}</span>
                      </span>
                      <span class="lt-lot-pill">Lot {{ lot.lot || '—' }}</span>
                    </header>
                    <dl class="lt-ptc-land-list">
                      <div v-for="field in lot.fields" :key="field.label"
                        :class="['lt-ptc-land-row', { 'lt-ptc-land-row-wide': field.wide }]">
                        <dt class="lt-ptc-land-label">{{ field.label }}</dt>
                        <dd class="lt-ptc-land-value">
                          <span v-if="field.type === 'status' && field.value" class="lt-status-pill"
                            :style="statusPillStyle(field.value)">
                            <span class="lt-status-dot" :style="{ background: statusColors(field.value).dot }"></span>
                            {{ field.value }}
                          </span>
                          <template v-else-if="field.value">{{ field.value }}</template>
                          <span v-else class="lt-fact-empty">—</span>
                        </dd>
                      </div>
                    </dl>
                  </section>
                </div>
                <p v-else class="lt-ptc-col-empty">No land sale lot recorded on this PTC.</p>
              </div>

              <!-- Right column: the components standing on those parcels -->
              <div class="lt-ptc-col">
                <div class="lt-ptc-col-head">
                  <span class="lt-eyebrow lt-eyebrow-muted">Project components (UTC)</span>
                  <span class="lt-ptc-col-count">{{ section.components.length }}</span>
                  <button v-if="canManageProject && section.fid" type="button" class="lt-col-add"
                    @click="openCollectionForm('component', section)">
                    + Component
                  </button>
                  <button v-if="canManageProject && section.components.length && sectionUtcUid(section)" type="button"
                    class="lt-col-add" @click="openSectionPermitForm('utc', section)">
                    + Permit Application
                  </button>
                </div>
                <div v-if="section.components.length" class="lt-component-grid">
                  <article v-for="comp in section.components" :key="comp.key" class="lt-card lt-component-card">
                    <header class="lt-component-head">
                      <span class="lt-component-mark"></span>
                      <div class="lt-component-head-info">
                        <span class="lt-eyebrow lt-eyebrow-inverse">{{ comp.label }}</span>
                        <span v-if="comp.address" class="lt-component-head-sub">{{ comp.address }}</span>
                      </div>
                      <h3 class="lt-component-title">{{ comp.name || '—' }}</h3>
                    </header>
                    <div class="lt-component-body">
                      <div class="lt-kv-row">
                        <span class="lt-kv-label">Type</span>
                        <span class="lt-kv-value">
                          <!-- Non-breaking spaces around the separator: the
                               template compiler condenses plain whitespace
                               between an interpolation and an element away. -->
                          {{ comp.type || '—' }}<template v-if="comp.storeys">&#160;<span
                              class="lt-dot-sep">·</span>&#160;{{ comp.storeys }}-storey</template>
                        </span>
                      </div>
                      <!-- The collection's own ids, shown so a reader can match a card
                           against the node: the group it sits in (field_group_id,
                           the key of its bucket under ptc.utc[serial]) and the
                           building it belongs to (field_building_group_id_). -->
                      <template v-if="comp.groupId || comp.buildingGroupId">
                        <div class="lt-kv-row">
                          <span class="lt-kv-label">Group ID</span>
                          <span class="lt-kv-value">{{ comp.groupId || '—' }}</span>
                        </div>
                        <div class="lt-kv-row">
                          <span class="lt-kv-label">Building (group ID)</span>
                          <span class="lt-kv-value">{{ comp.buildingGroupId || '—' }}</span>
                        </div>
                      </template>
                      <div class="lt-kv-row">
                        <span class="lt-kv-label">Status</span>
                        <div class="lt-status-cell">
                          <span class="lt-status-pill" :style="statusPillStyle(comp.status)">
                            <span class="lt-status-dot" :style="{ background: statusColors(comp.status).dot }"></span>
                            <a v-if="comp.refLink" :href="comp.refLink" target="_blank" rel="noopener noreferrer"
                              class="lt-status-link">{{ comp.status || 'Coming Soon' }}</a>
                            <template v-else>{{ comp.status || 'Coming Soon' }}</template>
                          </span>
                          <template v-if="comp.use">
                            <span class="lt-kv-value">{{ comp.use }}</span>
                          </template>
                          <template v-if="comp.units != null">
                            <span class="lt-dot-sep">·</span>
                            <span class="lt-kv-value lt-kv-strong">{{ formatNumber(comp.units) }} units</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
                <p v-else class="lt-ptc-col-empty">No project component recorded on this PTC.</p>
              </div>
            </div>
          </section>

          <div v-if="!ptcSections.length" class="lt-empty-state">No project type collections recorded.</div>

          <!-- Permit applications, one card per level the reference can hang
               off: the presale project, one of its type collections (a
               building), one of that collection's unit type collections (a
               component). One card per level rather than one section of one,
               because a presale milestone, a building's and a component's are
               not the same kind of record, and reading them in sequence invites
               treating the later ones as a continuation of the first. Oldest
               milestone first within a card; the markup is shared across the
               levels so they cannot drift into rendering the same value
               differently. -->
          <article v-for="section in permitSections" :key="section.level" class="lt-card lt-card-pad">
            <div class="lt-section-head">
              <span class="lt-section-dash"></span>
              <span class="lt-eyebrow lt-eyebrow-violet">Permit Applications</span>
              <h2 class="lt-section-title">{{ section.title }}</h2>
              <span v-if="section.permits.length" class="lt-count-pill">{{ section.permits.length }}</span>
            </div>

            <!-- Tabulator, mounted per level in initPermitTables. The wrapper
                 carries the Permit tab's own table classes so a milestone reads
                 the same here as it does there. -->
            <div v-show="section.permits.length" :ref="'permitTable-' + section.level"
              class="pt-table-wrap pt-process-wrap">
            </div>
            <div v-if="!section.permits.length" class="lt-empty-state">{{ section.empty }}</div>

            <p v-if="permitError && permitErrorLevel === section.level" class="lt-permit-error">{{ permitError }}</p>

            <!-- field_city_application_ hangs off the presale node, so its staff
                 reports belong with the presale milestones and nowhere else. -->
            <div v-if="section.level === 'project' && cityApplicationDocs.length" class="lt-permit-docs">
              <span class="lt-eyebrow lt-eyebrow-muted">City application documents</span>
              <div class="lt-permit-docs-list">
                <a v-for="(doc, idx) in cityApplicationDocs" :key="'city-doc-' + idx" class="lt-doc-chip"
                  :href="doc.uri" target="_blank" rel="noopener noreferrer">{{ doc.filename }}</a>
              </div>
            </div>
          </article>
        </div>

        <!-- LOT VIEW -->
        <div v-else-if="currentLot" class="lt-stack">
          <!-- Parcel Hero -->
          <div class="lt-hero">
            <div class="lt-hero-body">
              <div class="lt-hero-main">
                <span class="lt-eyebrow lt-eyebrow-onhero">Site address</span>
                <h2 class="lt-hero-title">{{ lotAddress(currentLot) }}</h2>
                <div class="lt-hero-meta">
                  <span class="lt-hero-chip">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="7" width="18" height="13" rx="2" />
                      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                    </svg>
                    {{ lotPidCount(currentLot) }} PIDs
                  </span>
                  <span v-if="currentLotLegalPreview" class="lt-hero-legal">{{ currentLotLegalPreview }}</span>
                  <span v-else class="lt-hero-legal">PID's &amp; Legal Description</span>
                  <button class="lt-hero-readmore" @click="toggleLotExpanded">
                    {{ lotExpanded ? 'Hide' : 'Read more' }}
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" stroke-linejoin="round"
                      :class="['lt-hero-readmore-chev', { 'is-open': lotExpanded }]">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="lt-hero-area">
                <span class="lt-eyebrow lt-eyebrow-onhero">Site area</span>
                <div class="lt-hero-area-value">
                  {{ lotSqftDisplay(currentLot) }}
                  <span class="lt-hero-area-unit">sqft</span>
                </div>
                <div class="lt-hero-area-acres">{{ lotAcresDisplay(currentLot) }} acres</div>
              </div>
            </div>

            <!-- Inline expand panel: PIDs + Legal description -->
            <div v-if="lotExpanded" class="lt-hero-expand">
              <div class="lt-hero-expand-col">
                <span class="lt-eyebrow lt-eyebrow-onhero">PIDs</span>
                <div v-if="currentLotPids.length" class="lt-hero-expand-chips">
                  <span v-for="(pid, idx) in currentLotPids" :key="'pid-' + idx"
                    class="lt-hero-expand-chip lt-hero-expand-chip-mono">{{ pid }}</span>
                </div>
                <span v-else class="lt-hero-expand-empty">—</span>
              </div>
              <div class="lt-hero-expand-col">
                <span class="lt-eyebrow lt-eyebrow-onhero">Legal description</span>
                <div v-if="currentLotLegalDescriptions.length" class="lt-hero-expand-chips">
                  <span v-for="(legal, idx) in currentLotLegalDescriptions" :key="'legal-' + idx"
                    class="lt-hero-expand-chip">{{
                      legal }}</span>
                </div>
                <span v-else class="lt-hero-expand-empty">—</span>
              </div>
            </div>

            <!-- Listing selector strip -->
            <div v-if="saleTypeOptions(currentLot).length" class="lt-hero-listings">
              <span class="lt-eyebrow lt-eyebrow-onhero">Listing</span>
              <div class="lt-listing-pills">
                <button v-for="opt in saleTypeOptions(currentLot)" :key="opt.value" class="lt-listing-pill"
                  :class="{ active: propertySelections[currentLotLandId] === opt.value }"
                  @click="setSelectedSaleType(currentLotLandId, opt.value)">
                  <span class="lt-listing-pill-label">{{ opt.text }}</span>
                  <span v-if="opt.landType" class="lt-listing-pill-badge">{{ opt.landType }}</span>
                </button>
              </div>
              <span v-if="currentListingListDate || currentListingBroker" class="lt-hero-listing-meta">
                <template v-if="currentListingListDate">Listed {{ currentListingListDate }}</template>
                <template v-if="currentListingListDate && currentListingBroker"> · </template>
                <template v-if="currentListingBroker">Agent {{ currentListingBroker }}</template>
              </span>
              <span v-if="currentLotStatus" class="lt-hero-status">
                <span class="lt-status-pill" :style="statusPillStyle(currentLotStatus)">
                  <span class="lt-status-dot" :style="{ background: statusColors(currentLotStatus).dot }"></span>
                  {{ currentLotStatus }}
                </span>
              </span>
            </div>
          </div>

          <!-- Deal + Zoning row -->
          <div class="lt-lot-grid">
            <article class="lt-card lt-card-pad">
              <div class="lt-section-head">
                <span class="lt-section-dash"></span>
                <span class="lt-eyebrow lt-eyebrow-violet">
                  Deal terms<template v-if="currentListingLabel"> · {{ currentListingLabel }}</template>
                </span>
                <h3 class="lt-section-title">Acquisition</h3>
              </div>
              <div class="lt-fact-grid">
                <div class="lt-fact">
                  <span class="lt-eyebrow lt-eyebrow-muted">Sale price</span>
                  <div
                    :class="['lt-fact-value lt-fact-accent', { 'lt-fact-empty': !isRealValue(currentReg.field_land_sale_price) }]">
                    {{ isRealValue(currentReg.field_land_sale_price) ? formatCurrency(currentReg.field_land_sale_price)
                      : '—' }}
                  </div>
                </div>
                <div class="lt-fact">
                  <span class="lt-eyebrow lt-eyebrow-muted">Contract date</span>
                  <div :class="['lt-fact-value', { 'lt-fact-empty': !isRealValue(currentReg.field_sold_firm_date) }]">
                    {{ isRealValue(currentReg.field_sold_firm_date) ? formatDate(currentReg.field_sold_firm_date) : '—'
                    }}
                  </div>
                </div>
                <div class="lt-fact">
                  <span class="lt-eyebrow lt-eyebrow-muted">Registration date</span>
                  <div :class="['lt-fact-value', { 'lt-fact-empty': !registrationDate }]">
                    {{ registrationDate || '—' }}
                  </div>
                </div>
                <div class="lt-fact">
                  <span class="lt-eyebrow lt-eyebrow-muted">Price per acre</span>
                  <div :class="['lt-fact-value', { 'lt-fact-empty': !pricePerAcre }]">
                    {{ pricePerAcre || '—' }}
                  </div>
                </div>
                <div class="lt-fact">
                  <span class="lt-eyebrow lt-eyebrow-muted">Per buildable sq.ft</span>
                  <div :class="['lt-fact-value', { 'lt-fact-empty': !perBuildableSqft }]">
                    {{ perBuildableSqft || '—' }}
                  </div>
                </div>
                <div class="lt-fact">
                  <span class="lt-eyebrow lt-eyebrow-muted">Price per unit</span>
                  <div :class="['lt-fact-value', { 'lt-fact-empty': !pricePerUnit }]">
                    {{ pricePerUnit || '—' }}
                  </div>
                </div>
              </div>
              <div class="lt-deal-foot">
                <span class="lt-deal-chip">
                  <span class="lt-deal-chip-label">Sale type</span>
                  <span class="lt-deal-chip-value">{{ currentListingSaleTypeBase || '—' }}</span>
                </span>
                <span class="lt-deal-chip-sep"></span>
                <span class="lt-deal-chip">
                  <span class="lt-deal-chip-label">Land type</span>
                  <span class="lt-deal-chip-value">{{ currentListingLandType || '—' }}</span>
                </span>
                <span class="lt-deal-chip-sep"></span>
                <span class="lt-deal-chip">
                  <span class="lt-deal-chip-label">List price</span>
                  <span class="lt-deal-chip-value">{{ isRealValue(currentListingListPrice) ?
                    formatCurrency(currentListingListPrice) : '—' }}</span>
                </span>
                <span class="lt-deal-chip-sep"></span>
                <span class="lt-deal-chip">
                  <span class="lt-deal-chip-label">Listed</span>
                  <span class="lt-deal-chip-value">{{ currentListingListDate || '—' }}</span>
                </span>
                <span class="lt-deal-chip-sep"></span>
                <span class="lt-deal-chip">
                  <span class="lt-deal-chip-label">Agent</span>
                  <span class="lt-deal-chip-value">{{ currentListingBroker || '—' }}</span>
                </span>
                <span class="lt-deal-chip-sep"></span>
                <span class="lt-deal-chip">
                  <span class="lt-deal-chip-label">Registered owner</span>
                  <span class="lt-deal-chip-value">{{ currentReg.field_ltsa_registered_owner || '—' }}</span>
                </span>
              </div>
            </article>

            <article class="lt-card lt-card-pad">
              <div class="lt-section-head">
                <span class="lt-section-dash"></span>
                <span class="lt-eyebrow lt-eyebrow-violet">Land use</span>
                <h3 class="lt-section-title">Zoning &amp; designation</h3>
              </div>
              <div class="lt-zoning-list">
                <div class="lt-zoning-row">
                  <span class="lt-zoning-label">Existing use</span>
                  <span :class="['lt-zoning-value', { 'lt-fact-empty': !currentReg.field_existing_use_ref }]">
                    {{ currentReg.field_existing_use_ref || '—' }}
                  </span>
                </div>
                <div class="lt-zoning-row">
                  <span class="lt-zoning-label">Existing zoning</span>
                  <span :class="['lt-zoning-value', { 'lt-fact-empty': !currentReg.field_existing_zoning_d }]">
                    {{ currentReg.field_existing_zoning_d || '—' }}
                  </span>
                </div>
                <div class="lt-zoning-row">
                  <span class="lt-zoning-label">Planning designation</span>
                  <span :class="['lt-zoning-value', { 'lt-fact-empty': !currentReg.field_city_based_planning_design }]">
                    {{ currentReg.field_city_based_planning_design || '—' }}
                  </span>
                </div>
              </div>
            </article>
          </div>
        </div>

        <div v-else class="lt-empty-state">Select a parcel to see details.</div>
      </section>
    </div>

    <!-- Add a milestone to this project, or edit one of its existing ones. -->
    <PermitApplicationModal ref="permitModal" modal-id="lt-permit-application" modal-class="lt-permit-modal"
      :presale-id="project.nid" :presale-title="project.project_title" :ptc-options="permitPtcOptions"
      :utc-options="permitUtcOptions" @saved="onPermitSaved" />

    <!-- Add a building to this project, or a component to one of its buildings. -->
    <b-modal id="lt-project-collection" hide-header hide-footer size="lg" modal-class="lt-collection-modal">
      <ProjectCollectionForm :key="collectionFormKey" :pid="project.nid" :project-title="project.project_title"
        :mode="collectionMode" :fid="collectionFid" :building-name="collectionBuildingName"
        @close="$bvModal.hide('lt-project-collection')" @saved="onCollectionSaved" />
    </b-modal>
  </div>
</template>

<script>
import { BACKEND_URL, isSuperAdmin } from '../variables.js';
import { initGoogleMapMulti } from '../shared/services';
import { useUserStore } from '../store/UserStore';
import { useProjectStore } from '../store/ProjectStore';
import { mapState } from 'pinia';
import PermitApplicationModal from './project-detail/PermitApplicationModal.vue';
import ProjectCollectionForm from './Forms/ProjectCollectionForm.vue';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { cbaItemsFromFlat, cbaStatusClass } from '../utilities/cbaStatus';

// Tabulator formatters return raw HTML strings, so anything read off a node
// goes through here first.
const escapeHtml = (str) => String(str == null ? "" : str)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const STATUS_COLOR = {
  'now selling': { dot: '#1F9D55', fg: '#0F7038', bg: 'rgba(31,157,85,0.10)' },
  'now selling 2': { dot: '#1F9D55', fg: '#0F7038', bg: 'rgba(31,157,85,0.10)' },
  'now leasing': { dot: '#1F9D55', fg: '#0F7038', bg: 'rgba(31,157,85,0.10)' },
  'now leasing 2': { dot: '#1F9D55', fg: '#0F7038', bg: 'rgba(31,157,85,0.10)' },
  'pre-lease': { dot: '#E0A800', fg: '#8B6800', bg: 'rgba(224,168,0,0.12)' },
  'leasing soon': { dot: '#E0A800', fg: '#8B6800', bg: 'rgba(224,168,0,0.12)' },
  'selling soon': { dot: '#E0A800', fg: '#8B6800', bg: 'rgba(224,168,0,0.12)' },
  'in planning': { dot: '#7E8B97', fg: '#3F4A57', bg: 'rgba(126,139,151,0.12)' },
  'coming soon': { dot: '#7E8B97', fg: '#3F4A57', bg: 'rgba(126,139,151,0.12)' },
  'sold out': { dot: '#D43847', fg: '#B82F3E', bg: 'rgba(212,56,71,0.10)' },
  'sold out 2': { dot: '#D43847', fg: '#B82F3E', bg: 'rgba(212,56,71,0.10)' },
  'leased out': { dot: '#D43847', fg: '#B82F3E', bg: 'rgba(212,56,71,0.10)' },
  'leased out 2': { dot: '#D43847', fg: '#B82F3E', bg: 'rgba(212,56,71,0.10)' },
  'active': { dot: '#1F9D55', fg: '#0F7038', bg: 'rgba(31,157,85,0.10)' },
  'inactive': { dot: '#D43847', fg: '#B82F3E', bg: 'rgba(212,56,71,0.10)' },
};
const STATUS_DEFAULT = { dot: '#7E8B97', fg: '#3F4A57', bg: 'rgba(126,139,151,0.12)' };

export default {
  components: { PermitApplicationModal, ProjectCollectionForm },
  data() {
    return {
      project: { land_info_new: [] },
      // The add-building / add-component form: remounted per open, and told
      // which level it is for. `collectionFid` names the building a component
      // goes into and is empty for a building.
      collectionFormKey: 0,
      collectionMode: 'building',
      collectionFid: null,
      collectionBuildingName: '',
      // The milestone a Remove is in flight for, so only its own button says so.
      removingPermitNid: null,
      // The permit tables, keyed by level. One Tabulator per card, built in
      // initPermitTables once the project's milestones are in.
      permitTables: {},
      permitError: '',
      // Which card a remove failure belongs to ('project' | 'ptc'), so the
      // message shows under the list the button was in and not the other one.
      permitErrorLevel: '',
      projectId: this.$route.query.project,
      landId: this.$route.query.land,
      propertySelections: {},
      activeLotId: this.$route.query.fid ? String(this.$route.query.fid) : 'total',
      // Which project type collection the roll-up view is showing. Empty until
      // the reader picks one, so the first section shows by default however the
      // backend orders its collections.
      activePtcKey: '',
      landInfo: {},
      lotExpanded: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['current_user']),
    isAdmin() {
      return ((this.current_user && this.current_user.role) || '').toLowerCase() === 'admin';
    },
    canEdit() {
      const role = ((this.current_user && this.current_user.role) || '').toLowerCase();
      return !!this.project.nid && ['admin', 'realtor'].includes(role);
    },
    // Admin only, independent of canEdit's wider role list: a realtor may open
    // the node in Drupal but may not write permit applications, buildings or
    // components from here. The real check is server-side — every one of those
    // endpoints answers Unauthorized to anyone who is not a Drupal
    // administrator or editor — so this only decides whether the controls are
    // worth showing.
    canManageProject() {
      return this.isAdmin && !!this.project.nid;
    },
    editHref() {
      return `${BACKEND_URL}node/${this.project.nid}/edit`;
    },
    cityName() {
      if (this.project.city) return this.project.city;
      const firstSite = (this.project.land_info_new || [])[0];
      return (firstSite && firstSite.field_city) || '';
    },
    projectTypeGroups() {
      return this.project.type_group ? Object.values(this.project.type_group) : [];
    },
    projectTotalData() {
      const out = {};
      this.projectTypeGroups.forEach((tg) => {
        const td = (tg.ptc && tg.ptc.total_data) || {};
        Object.entries(td).forEach(([type, m]) => {
          if (!out[type]) out[type] = { total_units: 0, total_floor_area: 0, total_parking: 0, gross_floor_area: 0 };
          out[type].total_units += parseInt(m.total_units) || 0;
          out[type].total_floor_area += parseFloat(m.total_floor_area) || 0;
          out[type].total_parking += parseInt(m.total_parking) || 0;
          out[type].gross_floor_area += parseFloat(m.gross_floor_area != null ? m.gross_floor_area : m.gross) || 0;
        });
      });
      return out;
    },
    projectTypeSummary() {
      return this.projectTypeGroups
        .map((tg) => (tg.ptc && tg.ptc.type) || '')
        .filter(Boolean)
        .join(', ');
    },
    developmentStats() {
      // Prefer top-level land_info_new (current backend shape).
      // Fallback to legacy nested type_group[*].ptc.land_info.
      let lots = this.project.land_info_new || [];
      if (!lots.length) {
        lots = this.projectTypeGroups.flatMap((tg) => (tg.ptc && tg.ptc.land_info) || []);
      }
      return lots.map((site, idx) => {
        const registrations = site.field_title_registration || [];
        const mainReg = registrations.length > 0 ? registrations[0] : {};
        const grouped = Object.groupBy([...registrations].reverse(),
          ({ field_bc_assessment_sale_type }) => field_bc_assessment_sale_type);
        const non_admin_reg = Object.values(grouped).flatMap((group) =>
          group.map((reg, index) => ({
            ...reg,
            _sale_type_base: reg.field_bc_assessment_sale_type,
            field_bc_assessment_sale_type: `${reg.field_bc_assessment_sale_type} ${index + 1}`,
          }))
        );
        return {
          ptc: {
            fid: site.land_id || `lot-${idx + 1}`,
            lot: idx + 1,
            type: this.projectTypeSummary,
            total_data: this.projectTotalData,
          },
          land_info_formatted: [{
            ...site,
            ...mainReg,
            land_id: site.land_id,
            non_admin_field_title_registration: non_admin_reg,
          }],
        };
      });
    },
    developmentTotals() {
      const groups = this.projectTypeGroups;
      if (!groups.length) return { totalArea: 0, totalUnits: 0, composition: [] };
      const typeComposition = {};
      let totalArea = 0;
      let totalUnits = 0;
      groups.forEach((tg) => {
        const data = (tg.ptc && tg.ptc.total_data) || {};
        Object.values(data).forEach((metrics) => {
          totalArea += parseFloat(metrics.total_floor_area) || 0;
          totalUnits += parseInt(metrics.total_units) || 0;
        });
        const typeStr = (tg.ptc && tg.ptc.type) || '';
        typeStr.split(',').forEach((part) => {
          const match = part.trim().match(/^(.*)\((\d+)\)$/);
          if (match) {
            const label = match[1].trim();
            const count = parseInt(match[2], 10);
            typeComposition[label] = (typeComposition[label] || 0) + count;
          }
        });
      });
      const composition = Object.entries(typeComposition).map(([label, total]) => `${label}(${total})`);
      return { totalArea, totalUnits, composition };
    },
    lotCount() {
      return this.developmentStats.length;
    },
    // One section per project type collection, in the order the backend sends
    // them: the parcels the PTC's building is approved on, and the unit type
    // collections standing on them. Both hang off the same type collection, so
    // they are read as one unit rather than as two flat lists — with two PTCs
    // on a site, a flat component list loses which building each one belongs
    // to.
    //
    // PTC and UTC numbers are project-wide running counts, not backend ids:
    // the second PTC's first component is UTC 3 when the first PTC has two,
    // which is how the collections are referred to on the node itself.
    ptcSections() {
      let utcNumber = 0;
      let parcelNumber = 0;
      return this.projectTypeGroups.map((tg, idx) => {
        const ptc = tg.ptc || {};
        const key = String(ptc.fid || `ptc-${idx + 1}`);
        const typeLabel = this.ptcTypeLabel(ptc.type);
        const lots = this.ptcLandLotRows(ptc, key, () => (parcelNumber += 1));
        const components = this.ptcComponentRows(tg, () => (utcNumber += 1));
        const name = ptc.name || typeLabel;
        return {
          key,
          // The collection's own id, kept apart from `key`: `key` falls back to
          // a positional label when a collection reports no fid, and a
          // component cannot be filed against a position.
          fid: ptc.fid ? String(ptc.fid) : '',
          label: `PTC ${idx + 1}`,
          name,
          typeLabel,
          // The tab reads as "20-0126 · Apartment" where both are known, and
          // falls back to whichever half the collection actually carries.
          navLabel: [name, typeLabel].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') || `PTC ${idx + 1}`,
          lots,
          components,
        };
      });
    },
    // The section on screen: the reader's pick while it still resolves to a
    // collection, otherwise the first one. Guards against a stale key after the
    // project reloads with a different set of collections.
    visiblePtcKey() {
      const sections = this.ptcSections;
      if (!sections.length) return '';
      const picked = sections.find((s) => s.key === this.activePtcKey);
      return picked ? picked.key : sections[0].key;
    },
    allComponents() {
      return this.ptcSections.flatMap((section) => section.components);
    },
    totalComponentCount() {
      return this.allComponents.length;
    },
    currentLot() {
      if (this.activeLotId === 'total') return null;
      return this.developmentStats.find((l) => String(l.ptc.fid) === this.activeLotId) || null;
    },
    currentLotLandId() {
      if (!this.currentLot) return null;
      const first = (this.currentLot.land_info_formatted || [])[0];
      return first ? first.land_id : null;
    },
    currentReg() {
      if (!this.currentLot) return {};
      const first = (this.currentLot.land_info_formatted || [])[0];
      if (!first) return {};
      const regs = first.non_admin_field_title_registration || [];
      const selected = this.propertySelections[first.land_id];
      const match = regs.find((r) => r.field_bc_assessment_sale_type === selected);
      return match || regs[0] || {};
    },
    currentLotStatus() {
      if (!this.currentLotLandId) return '';
      const li = this.landInfo[this.currentLotLandId];
      return li && li.status ? li.status : '';
    },
    registrationDate() {
      const li = this.landInfo && Object.values(this.landInfo)[0];
      if (!li || !li.reg) return null;
      const firstReg = Object.values(li.reg)[0];
      if (!firstReg || !firstReg.sale_date) return null;
      return this.formatDate(firstReg.sale_date);
    },
    pricePerAcre() {
      const reg = this.currentReg;
      const first = this.currentLot && (this.currentLot.land_info_formatted || [])[0];
      if (!this.isRealValue(reg.field_land_sale_price) || !this.isRealValue(first && first.field_lot_size_sqft_)) return null;
      const acres = first.field_lot_size_sqft_ * 0.0000229568;
      if (!acres) return null;
      return this.formatCurrency(Number(reg.field_land_sale_price) / acres);
    },
    perBuildableSqft() {
      const reg = this.currentReg;
      if (!this.isRealValue(reg.field_land_sale_price) || !this.isRealValue(this.developmentTotals.totalArea)) return null;
      return this.formatCurrency(Number(reg.field_land_sale_price) / this.developmentTotals.totalArea);
    },
    pricePerUnit() {
      const reg = this.currentReg;
      if (!this.isRealValue(reg.field_land_sale_price) || !this.developmentTotals.totalUnits) return null;
      return this.formatCurrency(Number(reg.field_land_sale_price) / this.developmentTotals.totalUnits);
    },
    currentListingLabel() {
      return (this.currentReg && this.currentReg.field_bc_assessment_sale_type) || '';
    },
    currentListingSaleTypeBase() {
      return (this.currentReg && this.currentReg._sale_type_base) || '';
    },
    currentListingLandType() {
      return (this.currentReg && this.currentReg.field_land_type) || '';
    },
    currentListingListPrice() {
      return this.currentReg && this.currentReg.field_land_list_price;
    },
    currentListingListDate() {
      const d = this.currentReg && this.currentReg.field_land_list_date;
      return d ? this.formatDate(d) : '';
    },
    currentListingBroker() {
      if (!this.currentReg) return '';
      const first = this.currentReg.field_first_broker_agent;
      const sold = this.currentReg.field_sold_broker_agent;
      const pick = (v) => {
        if (!v) return '';
        if (Array.isArray(v)) return v.filter(Boolean).join(', ');
        return String(v);
      };
      return pick(first) || pick(sold) || '';
    },
    currentLotPids() {
      if (!this.currentLot) return [];
      const first = (this.currentLot.land_info_formatted || [])[0];
      return this.fieldValues(first && first.field_ltsa_pid);
    },
    currentLotLegalDescriptions() {
      if (!this.currentLot) return [];
      const first = (this.currentLot.land_info_formatted || [])[0];
      return this.fieldValues(first && first.field_legal_description);
    },
    currentLotLegalPreview() {
      const items = this.currentLotLegalDescriptions;
      if (!items.length) return '';
      const shown = items.slice(0, 3).join(' · ');
      return items.length > 3 ? `${shown} · +${items.length - 3} more` : shown;
    },
    // get_single_project answers with the Permit Application nodes this project
    // references (field_application_ref) as three lists, one per level a
    // reference can hang off: `permits_project` for the development as a whole,
    // `permits_ptc` for a single building, `permits_utc` for one unit type
    // collection inside it. All are oldest first, and `nid` is what the editor
    // addresses on a save.
    projectLevelPermits() {
      return (this.project.permits_project || []).map(this.permitRow);
    },
    buildingLevelPermits() {
      return (this.project.permits_ptc || []).map(this.permitRow);
    },
    unitLevelPermits() {
      return (this.project.permits_utc || []).map(this.permitRow);
    },
    // The permit cards, one per level. `level` is the value permitRow carries
    // and the one a remove failure is filed under, so the error lands in the
    // card its button was in.
    permitSections() {
      return [
        {
          level: 'project',
          title: 'Presale',
          permits: this.projectLevelPermits,
          empty: 'No presale-level application recorded — rezoning, development permit '
            + 'and OCP amendment are filed against the project itself.',
        },
        {
          level: 'ptc',
          title: 'PTC',
          permits: this.buildingLevelPermits,
          empty: 'No PTC-level application recorded — a building permit is filed '
            + 'against the type collection for that building.',
        },
        {
          level: 'utc',
          title: 'UTC',
          permits: this.unitLevelPermits,
          empty: 'No UTC-level application recorded — file one here when a milestone '
            + 'covers a single component rather than the whole building.',
        },
      ];
    },
    // The project's type collections, for the form's placement selector: a
    // Building Permit hangs off a PTC, a Rezoning / DP off the project node
    // (the hierarchy planning_api.inc documents).
    permitPtcOptions() {
      return this.projectTypeGroups
        .map((tg) => tg.ptc || {})
        .filter((ptc) => ptc.fid)
        .map((ptc) => ({ fid: String(ptc.fid), name: ptc.name || ptc.type || '' }));
    },
    // The unit type collections of every type collection, flattened for the
    // same selector. `uid` is the field collection item id the save endpoint
    // takes; the PTC name comes along because a component is only readable
    // against the building it sits in.
    //
    // Read from type_group[*].utc, the flat sibling of `ptc`, which names the
    // item id outright. A backend that predates that falls back to
    // ptc.utc — the same rows, grouped by zoning serial then building group,
    // with the id only inside each row's ref_link.
    permitUtcOptions() {
      const seen = new Set();
      return this.projectTypeGroups.flatMap((tg) => {
        const ptc = tg.ptc || {};
        const ptcName = ptc.name || ptc.type || '';
        const flat = (tg.utc || []).map((utc) => ({
          uid: String(utc.uid || this.uidFromRefLink(utc.ref_link) || ''),
          fid: String(utc.fid || ptc.fid || ''),
          name: utc.name || utc.general_unit_type || '',
          ptcName: utc.ptc_name || ptcName,
        }));
        const rows = flat.some((utc) => utc.uid)
          ? flat
          : this.ptcUtcDetailRows(ptc).map((detail) => ({
            uid: String(this.uidFromRefLink(detail.ref_link) || ''),
            fid: String(ptc.fid || ''),
            name: detail.name || detail.type || '',
            ptcName,
          }));
        // A component reached through two zoning serials is still one
        // component, and a duplicate option would file a second milestone
        // against it without saying so.
        return rows.filter((utc) => {
          if (!utc.uid || seen.has(utc.uid)) return false;
          seen.add(utc.uid);
          return true;
        });
      });
    },
    // The City Application # nodes' staff reports (field_city_application_).
    cityApplicationDocs() {
      return (this.project.city_app_data || []).map((app) => {
        const file = app.planning_doc_file || {};
        return {
          uri: (file.fid && file.uri) || app.planning_doc || '',
          filename: file.filename || app.title || 'Document',
        };
      }).filter((doc) => doc.uri);
    },
  },
  watch: {
    '$route.query.fid'(newFid) {
      this.activeLotId = newFid ? String(newFid) : 'total';
    },
    developmentStats: {
      immediate: false,
      handler(stats) {
        const selections = { ...this.propertySelections };
        stats.forEach((lot) => {
          (lot.land_info_formatted || []).forEach((site) => {
            const regs = site.non_admin_field_title_registration || [];
            if (regs.length && !selections[site.land_id]) {
              selections[site.land_id] = regs[0].field_bc_assessment_sale_type;
            }
          });
        });
        this.propertySelections = selections;
        // Auto-correct activeLotId if a stale or legacy URL fid doesn't match any lot.
        if (this.activeLotId !== 'total' && stats.length) {
          const exists = stats.some((l) => String(l.ptc.fid) === this.activeLotId);
          if (!exists) this.activeLotId = String(stats[0].ptc.fid);
        }
      },
    },
    // The milestones arrive with the project, so the tables are built the
    // first time a section has rows and refreshed on every later change --
    // a save, a remove, or a reload onto another project.
    permitSections: {
      immediate: true,
      handler() {
        this.$nextTick(this.initPermitTables);
      },
    },
    // The Edit / Remove column only exists for an admin, and canManageProject
    // turns true when the project lands, after the first build.
    canManageProject() {
      this.$nextTick(this.initPermitTables);
    },
    // A remove in flight disables its own button and nothing else, so the
    // rows have to be re-rendered against the new value.
    removingPermitNid() {
      Object.keys(this.permitTables).forEach((level) => {
        const table = this.permitTables[level];
        const section = this.permitSections.find((s) => s.level === level);
        if (table && section) table.replaceData(section.permits);
      });
    },
  },
  mounted() {
    this.getProjectDetail();
  },
  beforeDestroy() {
    this.destroyPermitTables();
  },
  methods: {
    isRealValue(value) {
      return !['', null, 0, undefined].includes(value);
    },
    formatNumber(val) {
      return val ? new Intl.NumberFormat().format(val) : '';
    },
    formatCurrency(val) {
      return val ? new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
      }).format(val) : '';
    },
    formatDate(date) {
      if (!date || date.length <= 0 || date == null) return '';
      const rawDate = String(date);
      const parts = rawDate.split(' ')[0].split('-');
      if (parts.length !== 3) return rawDate;
      const [year, month, day] = parts;
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
    },
    statusColors(status) {
      return STATUS_COLOR[(status || '').toLowerCase()] || STATUS_DEFAULT;
    },
    statusPillStyle(status) {
      const c = this.statusColors(status);
      return { background: c.bg, color: c.fg };
    },
    lotAddress(lot) {
      const first = (lot && lot.land_info_formatted || [])[0];
      return (first && first.field_site_address) || 'Unnamed parcel';
    },
    lotSqftRaw(lot) {
      const first = (lot && lot.land_info_formatted || [])[0];
      return first && first.field_lot_size_sqft_ ? Number(first.field_lot_size_sqft_) : 0;
    },
    lotSqftDisplay(lot) {
      const v = this.lotSqftRaw(lot);
      return v ? this.formatNumber(v) : '—';
    },
    lotAcresDisplay(lot) {
      const v = this.lotSqftRaw(lot);
      if (!v) return '—';
      return (v * 0.0000229568).toFixed(3);
    },
    lotSubLine(lot) {
      const sqft = this.lotSqftRaw(lot);
      if (!sqft) return '—';
      return `${this.formatNumber(sqft)} sqft · ${this.lotAcresDisplay(lot)} ac`;
    },
    lotPidCount(lot) {
      const first = (lot && lot.land_info_formatted || [])[0];
      const pids = (first && first.field_ltsa_pid) || [];
      return Array.isArray(pids) ? pids.length : 0;
    },
    // The values of a repeating text field, which the backend sends as
    // [{ value, format, safe_value }] rows — the shape of every LTSA PID and
    // legal description on this page.
    fieldValues(field) {
      return (Array.isArray(field) ? field : [])
        .map((item) => (item && item.value != null ? item.value : item))
        .filter(Boolean);
    },
    // A PTC parcel's sale, read off its first title registration: what kind of
    // sale, for how much, on what date. Empty when none of the three is set.
    landSaleSummary(reg) {
      return [
        reg.field_bc_assessment_sale_type || '',
        this.isRealValue(reg.field_land_sale_price) ? this.formatCurrency(reg.field_land_sale_price) : '',
        this.isRealValue(reg.field_sold_firm_date) ? this.formatDate(reg.field_sold_firm_date) : '',
      ].filter(Boolean).join(' · ');
    },
    // The LTSA registration of that same transfer, when one is on record.
    ltsaRegistrationSummary(reg) {
      return [
        reg.field_registration_type || '',
        this.isRealValue(reg.field_registration_date) ? this.formatDate(reg.field_registration_date) : '',
      ].filter(Boolean).join(' · ');
    },
    // A type collection's project type without the unit counts the backend
    // appends on some endpoints ("Apartment(288)/Retail(2)" reads as
    // "Apartment · Retail"), so it sits beside the PTC name as a label rather
    // than a second set of figures.
    ptcTypeLabel(type) {
      return String(type || '')
        .split(/[,/]/)
        .map((part) => part.trim().replace(/\s*\(\d+\)\s*$/, ''))
        .filter(Boolean)
        .filter((part, idx, all) => all.indexOf(part) === idx)
        .join(' · ');
    },
    // The parcels one PTC's building is approved on, one card per land sale
    // collection. The PTC name travels with every card because a parcel is only
    // readable against the building approved on it, and two PTCs can carry the
    // same address. The lot number is the collection's own `lot` where it is
    // set; otherwise it falls back to `nextParcelNumber`, a project-wide
    // running count, since a land sale collection carries no number of its own.
    ptcLandLotRows(ptc, key, nextParcelNumber) {
      return (ptc.land_info || []).map((site, idx) => {
        const reg = (site.field_title_registration || [])[0] || {};
        const parcelNumber = nextParcelNumber();
        return {
          key: `${key}-${site.land_id || idx}`,
          ptcName: ptc.name || this.ptcTypeLabel(ptc.type),
          lot: this.isRealValue(ptc.lot) ? String(ptc.lot) : String(parcelNumber),
          fields: [
            { label: 'Land Sale (PTC)', value: this.landSaleSummary(reg) },
            { label: 'LTSA Address', value: site.field_site_address || '' },
            // Third, not fourth: the list is laid out two facts to a row and
            // this one takes the whole row, so at an even position it would
            // leave the half-row above it empty.
            { label: 'LTSA Legal Description', value: this.fieldValues(site.field_legal_description).join(' · '), wide: true },
            { label: 'LTSA PID', value: this.fieldValues(site.field_ltsa_pid).join(' · ') },
            { label: 'LTSA Parcel Status', value: site.field_ltsa_parcel_status || '', type: 'status' },
            { label: 'LTSA Lot Size (sqft)', value: this.formatNumber(site.field_lot_size_sqft_) },
            { label: 'LTSA Plan (SURVEY)', value: site.field_ltsa_plan_survey_ || '' },
            { label: 'LTSA Registration', value: this.ltsaRegistrationSummary(reg) },
          ],
        };
      });
    },
    // The unit type collections standing on one PTC, read off the `details`
    // rows of its default zoning group. `utc['0-Default']` can be either an
    // array or an object with numeric-string keys, so it is read through
    // Object.values; the storey count sits on the group, not the row.
    ptcComponentRows(tg, nextUtcNumber) {
      const ptc = tg.ptc || {};
      // Read through entries, not values: the bucket's key is the group id
      // (field_group_id on the unit type collection) and appears nowhere in the
      // rows themselves.
      const utcEntries = Object.entries((ptc.utc && ptc.utc['0-Default']) || {});
      const [firstGroupKey, firstGroup] = utcEntries[0] || ['', {}];
      const firstUtc = firstGroup || {};
      const groupId = (firstGroupKey && firstGroupKey !== 'zoning_info_only') ? String(firstGroupKey) : '';
      const storeys = firstUtc.storey || firstUtc.maxStorey || firstUtc.minStorey || '';
      return (firstUtc.details || []).map((d, idx) => {
        const number = nextUtcNumber();
        return {
          key: `${ptc.fid || 'ptc'}-utc-${this.uidFromRefLink(d.ref_link) || idx}`,
          label: `UTC ${number}`,
          name: d.name || '',
          type: d.project_type || ptc.type || '',
          status: d.status || '',
          use: d.type || '',
          units: (d.units != null && d.units !== '') ? d.units : null,
          refLink: d.ref_link || '',
          address: d.address || '',
          storeys,
          // Group id from the bucket key; building group id off the row, where
          // the backend sends field_building_group_id_ as `building`.
          groupId,
          buildingGroupId: (d.building != null && d.building !== '') ? String(d.building) : '',
        };
      });
    },
    saleTypeOptions(lot) {
      if (!lot) return [];
      const first = (lot.land_info_formatted || [])[0];
      if (!first) return [];
      return (first.non_admin_field_title_registration || []).map((r) => ({
        text: r.field_bc_assessment_sale_type,
        value: r.field_bc_assessment_sale_type,
        landType: r.field_land_type || '',
        saleTypeBase: r._sale_type_base || '',
      }));
    },
    toggleLotExpanded() {
      this.lotExpanded = !this.lotExpanded;
    },
    setSelectedSaleType(landId, value) {
      if (!landId) return;
      this.propertySelections = { ...this.propertySelections, [landId]: value };
    },
    selectLot(id) {
      this.activeLotId = id;
      this.lotExpanded = false;
      const newQuery = { ...this.$route.query };
      if (id === 'total') delete newQuery.fid;
      else newQuery.fid = id;
      this.$router.replace({ query: newQuery }).catch(() => { });
    },
    // The `details` rows of ptc.utc, flattened out of its two levels of keys:
    // zoning serial, then building group. Either level can come back as an
    // array or as an object with numeric-string keys, so both are read through
    // Object.values; the `zoning_info_only` group carries no details at all.
    ptcUtcDetailRows(ptc) {
      return Object.values((ptc && ptc.utc) || {})
        .flatMap((serial) => Object.values(serial || {}))
        .flatMap((group) => (group && group.details) || []);
    },
    // The unit type collection id out of a UTC row's `ref_link`
    // (/type?project=…&fid=…&uid=…). It is the only place the id appears on a
    // backend that does not send `uid` as a field of its own.
    uidFromRefLink(refLink) {
      const match = /[?&]uid=(\d+)/.exec(String(refLink || ''));
      return match ? match[1] : '';
    },
    // One row of either permit list. Shared so the two levels cannot drift into
    // rendering the same value differently; `key` is scoped to one list, which
    // is why the nid fallback carries the level.
    permitRow(permit, idx) {
      const file = permit.planning_doc_file || {};
      const rawDate = permit.date && permit.date !== '-' ? permit.date : '';
      const level = permit.level || 'project';
      return {
        key: permit.nid || `${level}-${idx}`,
        nid: permit.nid || null,
        // The node's own title. Not the same thing as `type`, which is derived
        // from the stage term's top parent -- a milestone is commonly titled
        // with its project number (PRJ17-013), which the type never carries.
        title: permit.title || '',
        type: permit.type || '',
        status: permit.status || '',
        permitNumber: permit.permit || '',
        address: permit.address || '',
        level,
        // PTC- and UTC-level rows: the building the milestone is filed against.
        fid: permit.fid || null,
        ptcName: permit.ptc_name || '',
        // UTC-level rows only: the component inside that building.
        uid: permit.uid || null,
        utcName: permit.utc_name || '',
        dateLabel: permit.date_iso ? this.formatDate(permit.date_iso) : rawDate,
        docUrl: (file.fid && file.uri) || permit.planning_doc || '',
        docLabel: file.filename || 'Planning doc',
        // The stage term's CBA statuses, already chips rather than the eight
        // flat keys the backend sends. They describe the stage, not this
        // application -- the same term is shared by every milestone that picks
        // it, which is why the Permit tab prints them read-only too.
        cbaItems: cbaItemsFromFlat(permit),
      };
    },
    // ── Permit Application tables ────────────────────────────────────────
    // One Tabulator per level, mounted into that level's card. The columns and
    // the cell markup are the Planning Process table of the project-detail
    // Permit tab (project-detail/PermitTab.vue), and the skin they share lives
    // in assets/styles/permit-table.css, so a milestone reads the same in both
    // places.
    permitSwatchClass(type) {
      const t = (type || '').toLowerCase();
      if (t.includes('rezon')) return 'pt-swatch--rez';
      if (t.includes('subdiv')) return 'pt-swatch--sub';
      if (t.includes('building')) return 'pt-swatch--bp';
      if (t.includes('develop')) return 'pt-swatch--dp';
      return 'pt-swatch--rez';
    },
    // The permit status is a planning stage, not a sales status, so it takes
    // the Permit tab's reading of it rather than STATUS_COLOR.
    permitPillClass(status) {
      const s = (status || '').toLowerCase();
      if (s.includes('approved') || s.includes('adopted')) return 'pt-pill--success';
      if (/in.?process|in.?progress/.test(s)) return 'pt-pill--progress';
      if (s.includes('carried')) return 'pt-pill--info';
      return 'pt-pill--neutral';
    },
    permitCbaClass(status) {
      return cbaStatusClass(status);
    },
    buildPermitColumns() {
      const self = this;
      const columns = [
        {
          title: 'Date',
          field: 'dateLabel',
          width: 150,
          headerSort: false,
          formatter: (cell) => {
            const data = cell.getData();
            const date = data.dateLabel || '—';
            // Same as the Planning Process table: the date carries the link to
            // the document, rather than a second column that is empty on most
            // rows.
            if (data.docUrl) {
              return `<a href="${escapeHtml(data.docUrl)}" target="_blank" rel="noopener noreferrer" `
                + `class="pt-date-link" title="${escapeHtml(data.docLabel)}">${escapeHtml(date)}</a>`;
            }
            const cls = data.dateLabel ? 'pt-date' : 'pt-date pt-date--muted';
            return `<span class="${cls}">${escapeHtml(date)}</span>`;
          },
        },
        {
          title: 'Application',
          field: 'type',
          width: 220,
          headerSort: false,
          formatter: (cell) => {
            const data = cell.getData();
            return `<span class="pt-typechip"><span class="pt-swatch ${self.permitSwatchClass(data.type)}"></span>`
              + `${escapeHtml(data.type || 'Application')}</span>`;
          },
        },
        {
          title: 'Reference',
          field: 'title',
          width: 420,
          headerSort: false,
          formatter: (cell) => {
            const data = cell.getData();
            // Chips, not a breadcrumb with arrows: a project number, a permit
            // number and the building or component a milestone is filed
            // against name the same record, they are not stages it moved
            // through.
            const chips = [data.title, data.permitNumber, data.ptcName, data.utcName]
              .filter(Boolean)
              .map((v) => `<span class="pt-step">${escapeHtml(v)}</span>`)
              .join('');
            const address = data.address
              ? `<span class="pt-outcome"><span class="pt-outcome-line">${escapeHtml(data.address)}</span></span>`
              : '';
            if (!chips && !address) return `<span class="pt-date--muted">—</span>`;
            return `<span class="pt-breadcrumb">${chips}${address}</span>`;
          },
        },
        {
          title: 'Status',
          field: 'status',
          width: 190,
          headerSort: false,
          formatter: (cell) => {
            const status = cell.getValue();
            if (!status) return `<span class="pt-date--muted">No stage</span>`;
            return `<span class="pt-pill ${self.permitPillClass(status)}"><span class="pt-dot"></span>`
              + `${escapeHtml(status)}</span>`;
          },
        },
        {
          // Same chips as the Permit tab's CBA Status column, left-aligned
          // because Actions follows it here rather than it ending the table.
          title: 'CBA Status',
          field: 'cbaItems',
          width: 220,
          headerSort: false,
          formatter: (cell) => {
            const items = cell.getValue() || [];
            if (!items.length) return `<span class="pt-date--muted">—</span>`;
            return items.map((item) =>
              `<span class="pt-cba ${self.permitCbaClass(item.status)}">`
              + `<strong>${escapeHtml(item.code)}</strong> ${escapeHtml(item.status)}</span>`
            ).join(' ');
          },
        },
      ];
      if (this.canManageProject) {
        columns.push({
          title: 'Actions',
          field: 'nid',
          width: 170,
          hozAlign: 'right',
          headerHozAlign: 'right',
          headerSort: false,
          formatter: (cell) => {
            const data = cell.getData();
            if (!data.nid) return '';
            const removing = self.removingPermitNid === data.nid;
            return '<span class="pt-actions">'
              + '<button type="button" class="pt-action pt-action--edit" data-permit-action="edit">Edit</button>'
              + `<button type="button" class="pt-action pt-action--remove" data-permit-action="remove"`
              + `${removing ? ' disabled' : ''}>${removing ? 'Removing…' : 'Remove'}</button>`
              + '</span>';
          },
          // The buttons are formatter HTML, so the cell is what hears the click.
          cellClick: (e, cell) => {
            const button = e.target.closest('[data-permit-action]');
            if (!button || button.disabled) return;
            const data = cell.getData();
            if (!data.nid) return;
            if (button.dataset.permitAction === 'edit') self.openPermitForm(data.nid);
            else self.removePermit(data);
          },
        });
      }
      return columns;
    },
    // Rebuilt rather than refreshed, because the Actions column appears with
    // canManageProject and a column set cannot be swapped in place.
    initPermitTables() {
      this.permitSections.forEach((section) => {
        // A ref inside a v-for comes back as an array.
        const held = this.$refs['permitTable-' + section.level];
        const element = Array.isArray(held) ? held[0] : held;
        const existing = this.permitTables[section.level];
        if (existing) existing.destroy();
        delete this.permitTables[section.level];
        if (!element || !section.permits.length) return;
        this.permitTables[section.level] = new Tabulator(element, {
          data: section.permits,
          layout: 'fitDataStretch',
          responsiveLayout: false,
          placeholder: "<div class='pt-empty'>No permit application recorded.</div>",
          columns: this.buildPermitColumns(),
        });
      });
    },
    destroyPermitTables() {
      Object.keys(this.permitTables).forEach((level) => {
        if (this.permitTables[level]) this.permitTables[level].destroy();
      });
      this.permitTables = {};
    },
    // No argument opens a blank application on the presale project itself; a
    // node id opens that milestone for editing.
    openPermitForm(nid) {
      this.$refs.permitModal.open({ nid });
    },
    // The first component of `section` that can actually be filed against —
    // a row without an item id is not addressable, so a button offering it
    // would open the form on a placement the save cannot name. Half of the UTC
    // button's gate: the column's own component list is the other half, because
    // this reads a separately built option list that can still name a component
    // the column shows nothing for.
    sectionUtcUid(section) {
      const fid = (section && section.fid) ? String(section.fid) : '';
      if (!fid) return '';
      const utc = this.permitUtcOptions.find((option) => option.fid === fid);
      return utc ? utc.uid : '';
    },
    // Opens a blank application starting on `section`: its building ('ptc') or
    // that building's first component ('utc'). The shared dialog's first step
    // still lets the user switch between the presale project, a building and a
    // component before saving.
    openSectionPermitForm(placement, section) {
      this.$refs.permitModal.open({
        placement,
        fid: (section && section.fid) ? section.fid : '',
        uid: placement === 'utc' ? this.sectionUtcUid(section) : '',
      });
    },
    // Pulls the milestone off this project and deletes the node behind it once
    // nothing else references it. Irreversible, so it asks first — and it is
    // scoped by pid, so a node another project also carries survives.
    async removePermit(permit) {
      if (!permit || !permit.nid || this.removingPermitNid) return;
      const label = [permit.type, permit.permitNumber].filter(Boolean).join(' ') || 'this application';
      // Deleting is a super admin action; any other admin files a request that
      // a super admin approves (the backend enforces it too).
      const canDelete = isSuperAdmin(useUserStore().getUser());
      const confirmed = await this.$bvModal.msgBoxConfirm(
        canDelete
          ? `Remove ${label} from this project? The permit application is deleted unless another project also carries it.`
          : `Request removal of ${label}? It stays on this project until a super admin approves the request.`,
        {
          title: canDelete ? 'Remove permit application' : 'Request removal',
          okTitle: canDelete ? 'Remove' : 'Send request',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        },
      );
      if (!confirmed) return;

      this.permitError = '';
      this.permitErrorLevel = '';
      this.removingPermitNid = permit.nid;
      try {
        const result = await useProjectStore().removeOrRequestPermitApplication(
          { nid: permit.nid, pid: this.project.nid, label },
          canDelete,
        );
        if (result.requested) {
          this.permitError = 'Removal requested. It takes effect once a super admin approves it.';
          this.permitErrorLevel = permit.level || 'project';
        } else {
          this.onPermitSaved();
        }
      } catch (err) {
        console.error('NewProjects removePermit error:', err);
        this.permitError = err.message || 'Could not remove the permit application.';
        this.permitErrorLevel = permit.level || 'project';
      } finally {
        this.removingPermitNid = null;
      }
    },
    // 'building' adds a project type collection to this project; 'component'
    // adds a unit type collection to the PTC `section` names.
    openCollectionForm(mode, section) {
      this.collectionMode = mode;
      this.collectionFid = (section && section.fid) || null;
      this.collectionBuildingName = (section && (section.name || section.label)) || '';
      this.collectionFormKey += 1;
      this.$bvModal.show('lt-project-collection');
    },
    // The building and component lists come from project.type_group, so re-read
    // the project to show what was written. The new collection is shown by
    // switching to it: a building added while a different tab was open is
    // otherwise created out of sight.
    onCollectionSaved(result) {
      this.getProjectDetail(() => {
        if (result && result.fid) this.activePtcKey = String(result.fid);
      });
    },
    onPermitSaved() {
      // The card reads project.permits_project / permits_ptc, so re-read the
      // project to show what was just written. The card lives in the roll-up
      // view, so switch to it through selectLot, which also drops the stale fid.
      this.getProjectDetail();
      this.selectLot('total');
    },
    // `onLoaded` runs once the payload is in — anything that has to read the
    // freshly loaded collections (selecting a building that did not exist a
    // moment ago) belongs there rather than after the call, which returns
    // before the request does.
    getProjectDetail(onLoaded) {
      $.get({ url: `${BACKEND_URL}api/get_single_project?pid=${this.projectId}` }, (data) => {
        if (!data) return;
        this.project = data;
        this.landInfo = data.land_info || {};
        const selections = {};
        this.developmentStats.forEach((lot) => {
          (lot.land_info_formatted || []).forEach((site) => {
            const regs = site.non_admin_field_title_registration || [];
            if (regs.length) selections[site.land_id] = regs[0].field_bc_assessment_sale_type;
          });
        });
        this.propertySelections = selections;
        if (typeof onLoaded === 'function') onLoaded(data);
        setTimeout(() => {
          const el = document.getElementById('map');
          if (el && this.project.type_group) initGoogleMapMulti(this.project.type_group, el);
        });
      });
    },
  },
};
</script>

<style scoped>
/* ---------------------------------------------------------------- *
 * Land Tab — local design tokens (scoped to this component)        *
 * Mirrors the design's palette without polluting global tokens.    *
 * ---------------------------------------------------------------- */
.land-tab {
  --lt-navy: #12283F;
  --lt-violet: #343376;
  --lt-coral: #FC5868;
  --lt-bg: #F4F6FA;
  --lt-surface: #FFFFFF;
  --lt-surface-sunken: #F7F8FB;
  --lt-fg-1: #12283F;
  --lt-fg-2: #545454;
  --lt-fg-3: #7E8B97;
  --lt-fg-soft: #C9CCD2;
  --lt-border: #E9E8E9;
  --lt-divider: #EEF0F4;
  --lt-violet-tint: #EDF2FE;
  --lt-violet-tint-strong: #FFE7EA;
  --lt-violet-fg: #343376;
  --lt-coral-fg: #B82F3E;

  font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--lt-bg);
  color: var(--lt-fg-1);
  padding: 24px;
  min-height: 100%;
  border-radius: 12px;
}

/* Page header */
.lt-page-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 22px;
  position: relative;
}

.lt-eyebrow-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.lt-accent-bar {
  width: 20px;
  height: 2px;
  background: var(--lt-coral);
  border-radius: 99px;
  flex-shrink: 0;
}

.lt-eyebrow {
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
}

.lt-eyebrow-violet {
  color: var(--lt-violet);
}

.lt-eyebrow-muted {
  color: var(--lt-fg-3);
}

.lt-eyebrow-inverse {
  color: rgba(255, 255, 255, 0.55);
}

.lt-eyebrow-onhero {
  color: rgba(255, 255, 255, 0.55);
}

.lt-title {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--lt-fg-1);
}

.lt-meta-line {
  margin-top: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: var(--lt-fg-2);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.lt-meta-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.lt-meta-sep {
  color: var(--lt-fg-soft);
}

.lt-header-actions {
  position: absolute;
  right: 0;
  top: 4px;
  display: flex;
  gap: 8px;
}

.lt-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  background: transparent;
  color: inherit;
}

.lt-btn-primary {
  background: var(--lt-navy);
  color: var(--lt-surface);
  border-color: var(--lt-navy);
}

.lt-btn-primary:hover {
  background: var(--lt-violet);
  border-color: var(--lt-violet);
}

.lt-btn-secondary {
  background: var(--lt-surface);
  color: var(--lt-fg-1);
  border-color: var(--lt-border);
}

.lt-btn-secondary:hover {
  background: var(--lt-surface-sunken);
  text-decoration: none;
  color: var(--lt-fg-1);
}

/* Layout */
.lt-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: stretch;
}

/* Lot Navigator: horizontal strip above the content */
.lt-lot-nav {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.lt-lot-nav-label {
  flex-shrink: 0;
  padding-left: 2px;
}

.lt-lot-nav-track {
  display: flex;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  padding-bottom: 4px;
}

.lt-lot-btn {
  text-align: left;
  cursor: pointer;
  min-width: 190px;
  flex-shrink: 0;
  padding: 13px 15px;
  border-radius: 10px;
  background: var(--lt-surface);
  color: var(--lt-fg-1);
  border: 1px solid var(--lt-border);
  display: flex;
  flex-direction: column;
  gap: 7px;
  position: relative;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1), color 120ms ease;
  font-family: 'Poppins', sans-serif;
}

.lt-lot-btn.active {
  background: var(--lt-navy);
  color: var(--lt-surface);
  border-color: var(--lt-navy);
}

.lt-lot-btn.active::before {
  content: '';
  position: absolute;
  top: 0;
  left: 15px;
  right: 15px;
  height: 2px;
  background: var(--lt-coral);
  border-radius: 99px;
}

.lt-lot-kicker {
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.lt-kicker-coral {
  background: var(--lt-violet-tint-strong);
  color: var(--lt-coral-fg);
}

.lt-kicker-violet {
  background: var(--lt-violet-tint);
  color: var(--lt-violet-fg);
}

.lt-lot-btn.active .lt-lot-kicker {
  background: rgba(255, 255, 255, 0.16);
  color: var(--lt-surface);
}

.lt-lot-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.lt-lot-sub {
  font-size: 11px;
  color: var(--lt-fg-3);
  line-height: 1.3;
}

.lt-lot-btn.active .lt-lot-sub {
  color: rgba(255, 255, 255, 0.62);
}

/* Content area */
.lt-content {
  flex: 1;
  min-width: 0;
}

.lt-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* Cards */
.lt-card {
  background: var(--lt-surface);
  border: 1px solid var(--lt-border);
  border-radius: 12px;
}

.lt-card-pad {
  padding: 18px 22px;
}

.lt-section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.lt-section-dash {
  width: 18px;
  height: 2px;
  background: var(--lt-coral);
  border-radius: 99px;
  flex-shrink: 0;
}

.lt-section-title {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--lt-fg-1);
  letter-spacing: -0.01em;
}

.lt-section-body {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: var(--lt-fg-2);
  line-height: 1.5;
  max-width: 560px;
}

.lt-composition-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 14px;
}

.lt-chip {
  padding: 4px 10px;
  border-radius: 99px;
  background: var(--lt-violet-tint);
  color: var(--lt-violet-fg);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
}

/* ---------------------------------------------------------------- *
 * Project type collections                                         *
 * A PTC's parcels beside the components standing on them: the       *
 * parcels in a fixed-width left column so their long LTSA values    *
 * stay readable, the components in a flexible grid beside them.     *
 * ---------------------------------------------------------------- */
.lt-ptc-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  background: var(--lt-surface);
  border: 1px solid var(--lt-border);
  border-radius: 12px;
  overflow-x: auto;
}

.lt-ptc-nav-btn {
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 16px;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--lt-fg-2);
  white-space: nowrap;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1), color 120ms cubic-bezier(0.2, 0, 0, 1);
}

.lt-ptc-nav-btn:hover {
  background: var(--lt-surface-sunken);
  color: var(--lt-fg-1);
}

.lt-ptc-nav-kicker {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--lt-fg-3);
}

.lt-ptc-nav-name {
  letter-spacing: -0.01em;
}

.lt-ptc-nav-count {
  font-size: 11px;
  font-weight: 600;
  color: var(--lt-fg-2);
  background: var(--lt-surface-sunken);
  border: 1px solid var(--lt-border);
  border-radius: 99px;
  padding: 1px 7px;
}

.lt-ptc-nav-btn.active {
  background: var(--lt-navy);
  color: #FFFFFF;
}

.lt-ptc-nav-btn.active .lt-ptc-nav-kicker {
  color: var(--lt-coral);
}

.lt-ptc-nav-btn.active .lt-ptc-nav-count {
  color: #FFFFFF;
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.12);
}

.lt-ptc-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 18px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--lt-divider);
}

.lt-ptc-title {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--lt-fg-1);
}

.lt-ptc-meta {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: var(--lt-fg-2);
}

.lt-ptc-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

.lt-ptc-cols {
  display: grid;
  grid-template-columns: minmax(340px, 420px) 1fr;
  gap: 28px;
  align-items: start;
}

.lt-ptc-col {
  min-width: 0;
}

.lt-ptc-col-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.lt-ptc-col-count {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--lt-fg-2);
  background: var(--lt-surface-sunken);
  border: 1px solid var(--lt-border);
  border-radius: 99px;
  padding: 1px 7px;
}

/* The column's own add control. Pushed to the far edge so it reads as an
   action on the list rather than another count beside the heading. */
.lt-col-add {
  margin-left: auto;
  padding: 3px 9px;
  border: 1px dashed var(--lt-border);
  border-radius: 99px;
  background: transparent;
  color: var(--lt-fg-2);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.lt-col-add:hover {
  border-style: solid;
  border-color: var(--lt-violet);
  color: var(--lt-violet);
}

/* In the collection header the counts already hold the auto margin, so this
   one sits directly beside them rather than pushing to a second edge. */
.lt-head-add {
  margin-left: 0;
}

.lt-ptc-col-empty {
  margin: 0;
  padding: 18px;
  border: 1px dashed var(--lt-border);
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 12.5px;
  color: var(--lt-fg-3);
}

.lt-ptc-land-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.lt-ptc-land-card {
  background: var(--lt-surface-sunken);
  border: 1px solid var(--lt-border);
  border-radius: 8px;
  overflow: hidden;
}

.lt-ptc-land-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 16px;
  border-bottom: 1px solid var(--lt-divider);
}

.lt-ptc-land-ident {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.lt-ptc-land-name {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--lt-fg-1);
  overflow-wrap: anywhere;
}

.lt-lot-pill {
  flex: none;
  padding: 3px 9px;
  border-radius: 4px;
  background: var(--lt-violet-tint);
  color: var(--lt-violet-fg);
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
}

/* Two facts per row: eight stacked label/value pairs made the parcel card
   twice the height of the components beside it, and every value but the legal
   description is short enough to read at half the width. */
.lt-ptc-land-list {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.lt-ptc-land-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 11px 16px;
  /* Drawn on top rather than bottom: a grid's last row can hold one cell or
     two, so there is no `:last-child` that reliably closes a bottom rule. */
  border-top: 1px solid var(--lt-divider);
}

/* The first row sits against the card header, which draws its own rule. */
.lt-ptc-land-row:nth-child(-n+2) {
  border-top: 0;
}

/* A sentence, not a value — at half width it wraps to four lines and costs
   more height than the row it saves. */
.lt-ptc-land-row-wide {
  grid-column: 1 / -1;
}

.lt-ptc-land-label {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--lt-fg-3);
}

.lt-ptc-land-value {
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--lt-fg-1);
  overflow-wrap: anywhere;
}

/* Component card (UTC) — sits inside a PTC's right-hand column, so the grid
   tracks are narrower than a full-width card would need */
.lt-component-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.lt-component-card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(18, 40, 63, 0.06);
}

.lt-component-head {
  padding: 13px 16px;
  background: var(--lt-navy);
  color: var(--lt-surface);
  display: flex;
  align-items: center;
  gap: 10px;
}

.lt-component-mark {
  width: 11px;
  height: 11px;
  border: 2px solid var(--lt-coral);
  border-radius: 2px;
  flex-shrink: 0;
}

.lt-component-head-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.lt-component-head-sub {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
}

.lt-component-title {
  margin: 0 0 0 auto;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
  text-align: right;
}

.lt-component-body {
  padding: 0;
}

.lt-kv-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px;
  gap: 10px;
  border-bottom: 1px solid var(--lt-divider);
}

.lt-kv-row:last-child {
  border-bottom: none;
}

.lt-kv-label {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: var(--lt-fg-2);
}

.lt-kv-value {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--lt-fg-1);
}

.lt-kv-strong {
  font-weight: 700;
}

.lt-status-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.lt-dot-sep {
  color: var(--lt-fg-soft);
}

.lt-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 8px;
  border-radius: 99px;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.lt-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 99px;
}

.lt-status-link {
  color: inherit;
  text-decoration: none;
}

.lt-status-link:hover {
  text-decoration: underline;
}

/* Hero */
.lt-hero {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--lt-navy);
  background: var(--lt-navy);
  color: var(--lt-surface);
}

.lt-hero-body {
  padding: 18px 22px;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  flex-wrap: wrap;
}

.lt-hero-main {
  flex: 1;
  min-width: 220px;
}

.lt-hero-title {
  margin: 5px 0 0;
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.lt-hero-meta {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.lt-hero-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.10);
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
}

.lt-hero-legal {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.lt-hero-readmore {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: var(--lt-surface);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}

.lt-hero-readmore:hover {
  background: rgba(255, 255, 255, 0.06);
}

.lt-hero-readmore-chev {
  transition: transform 160ms ease;
}

.lt-hero-readmore-chev.is-open {
  transform: rotate(180deg);
}

.lt-hero-area {
  text-align: right;
}

.lt-hero-area-value {
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-top: 3px;
}

.lt-hero-area-unit {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.lt-hero-area-acres {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.lt-hero-expand {
  padding: 14px 22px;
  background: rgba(0, 0, 0, 0.18);
  border-top: 1px solid rgba(255, 255, 255, 0.10);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}

.lt-hero-expand-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.lt-hero-expand-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.lt-hero-expand-chip {
  padding: 3px 9px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.10);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: var(--lt-surface);
  word-break: break-word;
}

.lt-hero-expand-chip-mono {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.lt-hero-expand-empty {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
}

.lt-hero-listings {
  padding: 10px 22px;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.10);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.lt-listing-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.lt-listing-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.10);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 120ms ease;
}

.lt-listing-pill:hover {
  background: rgba(255, 255, 255, 0.16);
}

.lt-listing-pill.active {
  background: var(--lt-surface);
  color: var(--lt-navy);
}

.lt-listing-pill-label {
  white-space: nowrap;
}

.lt-listing-pill-badge {
  padding: 1px 7px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
}

.lt-listing-pill.active .lt-listing-pill-badge {
  background: var(--lt-violet-tint);
  color: var(--lt-violet-fg);
}

.lt-hero-listing-meta {
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lt-hero-status {
  margin-left: auto;
}

/* Deal + Zoning grid */
.lt-lot-grid {
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 18px;
  align-items: start;
}

.lt-fact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 18px;
}

.lt-fact {
  min-width: 0;
}

.lt-fact-value {
  font-family: 'Poppins', sans-serif;
  margin-top: 5px;
  line-height: 1.1;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--lt-fg-1);
}

.lt-fact-accent {
  color: var(--lt-violet);
}

.lt-fact-empty {
  color: var(--lt-fg-soft);
  font-weight: 400;
}

.lt-deal-foot {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--lt-divider);
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  align-items: center;
}

.lt-deal-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 11.5px;
}

.lt-deal-chip-label {
  color: var(--lt-fg-3);
}

.lt-deal-chip-value {
  color: var(--lt-fg-1);
  font-weight: 600;
}

.lt-deal-chip-sep {
  width: 1px;
  height: 12px;
  background: var(--lt-border);
}

.lt-zoning-list {
  margin-top: -4px;
}

.lt-zoning-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 0;
  border-top: 1px solid var(--lt-divider);
}

.lt-zoning-row:first-child {
  border-top: none;
  padding-top: 0;
}

.lt-zoning-label {
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: var(--lt-fg-2);
}

.lt-zoning-value {
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--lt-fg-1);
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--lt-surface-sunken);
  max-width: 60%;
  text-align: right;
  word-break: break-word;
}

.lt-zoning-value.lt-fact-empty {
  background: transparent;
  font-weight: 400;
}

/* Count pill */
.lt-count-pill {
  padding: 2px 8px;
  border-radius: 99px;
  background: var(--lt-violet-tint);
  color: var(--lt-violet-fg);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 700;
}

/* Permit applications — the milestone rows themselves are a Tabulator styled
   by assets/styles/permit-table.css; what is left here is the card furniture
   around it. Only the seam between the card and the table is set here: the
   table draws its own rules, so the card must not draw a second one. */
.lt-card-pad .pt-table-wrap {
  margin-top: 4px;
  border-top: 1px solid var(--lt-divider);
}

.lt-permit-error {
  margin: 10px 0 0;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--lt-coral-fg);
}

.lt-permit-docs {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--lt-divider);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lt-permit-docs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.lt-doc-chip {
  padding: 4px 10px;
  border-radius: 99px;
  border: 1px solid var(--lt-border);
  background: var(--lt-surface-sunken);
  color: var(--lt-fg-2);
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  text-decoration: none;
}

.lt-doc-chip:hover {
  color: var(--lt-violet-fg);
  border-color: var(--lt-violet-fg);
}

.lt-empty-state {
  padding: 28px;
  text-align: center;
  color: var(--lt-fg-3);
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  border: 1px dashed var(--lt-border);
  border-radius: 12px;
  background: var(--lt-surface);
}

/* ---------------------------------------------------------------- *
 * Responsive                                                       *
 * ---------------------------------------------------------------- */
@media (max-width: 992px) {
  .lt-lot-grid {
    grid-template-columns: 1fr;
  }

  .lt-fact-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Past this width the two columns squeeze the LTSA values into
     unreadable ribbons, so the parcels stack above their components. */
  .lt-ptc-cols {
    grid-template-columns: 1fr;
    gap: 22px;
  }
}

@media (max-width: 768px) {
  .land-tab {
    padding: 16px;
  }

  .lt-layout {
    gap: 16px;
  }

  .lt-lot-nav-label {
    display: none;
  }

  .lt-lot-btn {
    min-width: 170px;
  }

  .lt-title {
    font-size: 24px;
  }

  .lt-header-actions {
    position: static;
    margin-top: 12px;
  }

  .lt-hero-area {
    text-align: left;
  }

  .lt-component-grid {
    grid-template-columns: 1fr;
  }

  .lt-ptc-counts {
    margin-left: 0;
    width: 100%;
  }
}

@media (max-width: 520px) {
  .lt-fact-grid {
    grid-template-columns: 1fr;
  }

  .lt-component-head {
    flex-wrap: wrap;
  }

  .lt-component-title {
    margin-left: 0;
  }

  .lt-component-head-sub {
    max-width: none;
    white-space: normal;
  }

  .lt-zoning-value {
    max-width: 50%;
  }

  .lt-hero-expand {
    grid-template-columns: 1fr;
  }

  .lt-hero-listing-meta {
    width: 100%;
  }

  .lt-ptc-head {
    gap: 8px;
  }

  .lt-ptc-land-top {
    flex-direction: column;
  }

  .lt-ptc-land-list {
    grid-template-columns: 1fr;
  }

  .lt-ptc-land-row:nth-child(-n+2) {
    border-top: 1px solid var(--lt-divider);
  }

  .lt-ptc-land-row:first-child {
    border-top: 0;
  }
}

/* ---------------------------------------------------------------- *
 * Dark mode overrides (html.dark)                                  *
 * ---------------------------------------------------------------- */
:global(html.dark) .land-tab,
html.dark .land-tab {
  --lt-bg: #0E1B2C;
  --lt-surface: #15273D;
  --lt-surface-sunken: #1A2638;
  --lt-fg-1: #E6EEF8;
  --lt-fg-2: #B8C5D6;
  --lt-fg-3: #8AA0B8;
  --lt-fg-soft: #5A7795;
  --lt-border: rgba(255, 255, 255, 0.08);
  --lt-divider: rgba(255, 255, 255, 0.06);
  --lt-violet-tint: rgba(52, 51, 118, 0.22);
  --lt-violet-tint-strong: rgba(252, 88, 104, 0.22);
}

html.dark .lt-lot-btn {
  background: var(--lt-surface);
}

html.dark .lt-lot-btn.active {
  background: #243a55;
  border-color: rgba(255, 255, 255, 0.12);
}

html.dark .lt-card {
  background: var(--lt-surface);
}

html.dark .lt-component-head {
  background: #0B1727;
}

html.dark .lt-component-card {
  box-shadow: none;
}

html.dark .lt-ptc-nav {
  background: var(--lt-surface);
}

html.dark .lt-ptc-nav-btn.active {
  background: #0B1727;
}

/* The violet-on-tint pairing is a light-mode reading; on the dark tint the
   ink has to come up to stay legible. */
html.dark .lt-chip,
html.dark .lt-lot-pill {
  color: #B7B6E6;
}

/* --lt-violet is not re-declared for dark, so the hover accent is taken from
   the same lightened violet the chips use rather than the near-black token. */
html.dark .lt-col-add:hover {
  border-color: #B7B6E6;
  color: #B7B6E6;
}

html.dark .lt-zoning-value {
  background: rgba(255, 255, 255, 0.04);
}

html.dark .lt-hero {
  background: #0B1727;
  border-color: rgba(255, 255, 255, 0.10);
}

html.dark .lt-btn-secondary {
  background: var(--lt-surface);
  color: var(--lt-fg-1);
}

html.dark .lt-btn-secondary:hover {
  background: #243a55;
}

html.dark .lt-empty-state {
  background: var(--lt-surface);
  border-color: rgba(255, 255, 255, 0.10);
}

html.dark .lt-doc-chip {
  background: rgba(255, 255, 255, 0.04);
}

html.dark .lt-permit-error {
  color: #FFB3BB;
}
</style>
